/**
 * StudioBulkReview — Studio replacement for CatalogReviewStep.
 * Groups catalog entries by type with per-group progress bars, confidence
 * bucket filter toggles, per-row selection, and Apply/Reject actions.
 */

import { useState, useMemo, useCallback } from 'react';
import type { JSX } from 'react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n/I18nContext.js';
import type { TranslationKeys } from '@/i18n/types.js';
import type { CatalogEntry, CampaignEntityType } from '@roll1/shared';
import { Checkbox } from '../ui/checkbox';

interface StudioBulkReviewProps {
  entries: CatalogEntry[];
  onConfirm: (selected: CatalogEntry[]) => void;
  onReject: () => void;
  onBack: () => void;
  onCancel: () => void;
}

/** Ordered entity types used for stable group rendering. */
const GROUP_ORDER: CampaignEntityType[] = ['npc', 'location', 'note', 'journal', 'encounter'];

/** i18n key per entity type group heading. */
const GROUP_LABEL_KEY: Record<CampaignEntityType, keyof TranslationKeys> = {
  npc: 'studio.start.bulk.group.npc',
  location: 'studio.start.bulk.group.location',
  note: 'studio.start.bulk.group.note',
  journal: 'studio.start.bulk.group.journal',
  encounter: 'studio.start.bulk.group.encounter',
};

type ConfBucket = 'high' | 'mid' | 'low';

const CONF_BUCKETS: ConfBucket[] = ['high', 'mid', 'low'];

const CONF_LABEL_KEY: Record<ConfBucket, keyof TranslationKeys> = {
  high: 'studio.start.bulk.conf.high',
  mid: 'studio.start.bulk.conf.mid',
  low: 'studio.start.bulk.conf.low',
};

/** Classify an entry's confidence into a bucket. */
function bucketOf(confidence: number): ConfBucket {
  if (confidence >= 80) return 'high';
  if (confidence >= 50) return 'mid';
  return 'low';
}

/** Replace named placeholders like {count} in a raw translation string. */
function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
  );
}

/** Stable identity for an entry within this session. */
function keyOf(entry: CatalogEntry, index: number): string {
  return `${entry.type}:${entry.slug}:${index}`;
}

export function StudioBulkReview({
  entries,
  onConfirm,
  onReject,
  onBack,
  onCancel,
}: StudioBulkReviewProps): JSX.Element {
  const { t } = useI18n();

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(entries.map((entry, index) => keyOf(entry, index))),
  );
  const [activeBuckets, setActiveBuckets] = useState<Set<ConfBucket>>(
    () => new Set<ConfBucket>(CONF_BUCKETS),
  );

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleBucket = useCallback((bucket: ConfBucket) => {
    setActiveBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) {
        next.delete(bucket);
      } else {
        next.add(bucket);
      }
      return next;
    });
  }, []);

  /** Entries paired with a stable id, kept in original order. */
  const indexed = useMemo(
    () => entries.map((entry, index) => ({ id: keyOf(entry, index), entry })),
    [entries],
  );

  /** Rows visible under the current confidence-bucket filter. */
  const visible = useMemo(
    () => indexed.filter(({ entry }) => activeBuckets.has(bucketOf(entry.confidence))),
    [indexed, activeBuckets],
  );

  /** Visible rows grouped by entity type, in GROUP_ORDER. */
  const groups = useMemo(() => {
    return GROUP_ORDER.map((type) => {
      const rows = visible.filter(({ entry }) => entry.type === type);
      const selectedCount = rows.filter(({ id }) => selected.has(id)).length;
      return { type, rows, total: rows.length, selectedCount };
    }).filter((group) => group.total > 0);
  }, [visible, selected]);

  const selectedEntries = useMemo(
    () => visible.filter(({ id }) => selected.has(id)).map(({ entry }) => entry),
    [visible, selected],
  );

  const selectedTotal = selectedEntries.length;
  const rejectedTotal = visible.length - selectedTotal;

  const handleApply = useCallback(() => {
    onConfirm(selectedEntries);
  }, [onConfirm, selectedEntries]);

  return (
    <div className="space-y-4">
      <p className="text-ui font-medium">
        {interpolate(t('studio.start.bulk.selected'), {
          selected: selectedTotal,
          total: visible.length,
        })}
      </p>

      <div className="conf-row flex flex-wrap gap-2">
        {CONF_BUCKETS.map((bucket) => {
          const active = activeBuckets.has(bucket);
          return (
            <button
              key={bucket}
              type="button"
              aria-pressed={active}
              onClick={() => toggleBucket(bucket)}
              className={`rounded-md border px-3 py-1 text-meta transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-muted text-muted-foreground opacity-60'
              }`}
            >
              {t(CONF_LABEL_KEY[bucket])}
            </button>
          );
        })}
      </div>

      <div className="max-h-[400px] space-y-4 overflow-auto">
        {groups.map((group) => {
          const ratio = group.total > 0 ? group.selectedCount / group.total : 0;
          return (
            <div key={group.type} className="bgroup space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-ui font-medium">{t(GROUP_LABEL_KEY[group.type])}</span>
                <span className="text-meta text-muted-foreground">
                  {group.selectedCount}/{group.total}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="bbar h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.round(ratio * 100)}%` }}
                />
              </div>
              <ul className="space-y-1">
                {group.rows.map(({ id, entry }) => {
                  const isSelected = selected.has(id);
                  return (
                    <li key={id}>
                      <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 hover:bg-muted/50">
                        <Checkbox
                          className="mt-0.5"
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(id)}
                        />
                        <span className="flex-1">
                          <span className="block text-ui">{entry.name}</span>
                          {entry.briefDescription ? (
                            <span className="block text-meta text-muted-foreground">
                              {entry.briefDescription}
                            </span>
                          ) : null}
                        </span>
                        <span className="text-meta text-muted-foreground">{entry.confidence}%</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onBack}>
          {t('common.back')}
        </Button>
        <Button variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="outline" disabled={rejectedTotal === 0} onClick={onReject}>
          {interpolate(t('studio.start.bulk.reject'), { count: rejectedTotal })}
        </Button>
        <Button disabled={selectedTotal === 0} onClick={handleApply}>
          {interpolate(t('studio.start.bulk.apply'), { count: selectedTotal })}
        </Button>
      </div>
    </div>
  );
}
